import React, {useContext, useEffect, useState} from 'react';
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Context } from "../../commons/context";
import AutocompleteInput from "../_shared/AutocompleteInput";
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import EditTitle from '../_shared/EditTitle';
import Loader from '../_shared/Loader';
import PopUp from '../_shared/PopUp';
import JwtInterceptor from '../_shared/JwtInterceptors';
import {pl} from "date-fns/locale";
registerLocale("pl", pl);

const API_URL = process.env.REACT_APP_API_URL;

const TaxFormForm = () => {
  const { t } = useTranslation();
  const { control, register, handleSubmit, reset, watch, formState: { errors } } = useForm();
  const [data] = useContext(Context);
  const [loader, setLoader] = useState(true);
  const [isEdit, setIsEdit] = useState(false);
  const [popUp, setPopUp] = useState(false);
  const [popUpText, setPopUpText] = useState('');
  const [popUpType, setPopUpType] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [radioVal, setRadioVal] = useState();
  const [initRadioVal, setInitRadioVal] = useState();
  const [refreshData, setRefreshData] = useState(false);
  const [userData, setUserData] = useState([]);
  const [userDataId, setUserDataId] = useState(0);
  const [us, setUs] = useState('');
  const [usList, setUsList] = useState([]);
  const [usSelected, setUsSelected] = useState('');
  const [usError, setUsError] = useState(false);
  const [birthdate, setBirthdate] = useState();
  let dpickerLocal = 'pl';

  useEffect(() => {
    JwtInterceptor
      .get(`${API_URL}Dictionary/taxOffices`)
      .then((respond) => {
        setUsList(respond.data);
        setSuggestions(respond.data.map(s => s.name));
      })
      .catch(function (error) {
        console.log(error);
      }).finally(() => {
        setLoader(false);
      });
  }, []);

  useEffect(() => {
    if (data.user.id || refreshData) {
      setLoader(true);
      JwtInterceptor
          .get(`${API_URL}Users/${data.user.id}/taxData`)
          .then((respond) => {
            setBirthdate(respond.data.dateOfBirth ? new Date(respond.data.dateOfBirth) : new Date());
            setUserData(respond.data);
            setUserDataId(respond.data.id);

            if (respond.data.isTaxResidence === false) {
              setRadioVal('2');
              setInitRadioVal('2');
            } else {
              setRadioVal('1');
              setInitRadioVal('1');
            }

            if (!respond.data.identityCard) setIsEdit(true);

            reset();
          })
          .catch(function (error) {
            console.log(error);
          }).finally(() => {
            setLoader(false);
            setRefreshData(false);
      });
    }
  }, [data.user.id, refreshData]);

  useEffect(() => {
    if (birthdate) {
      reset({
        birthdate: new Date(birthdate),
      });
    }
  }, [birthdate]);

  useEffect(() => {
    if (usList.length > 0 && userData.taxOfficeId) {
      const u = usList.filter(us => us.id === userData.taxOfficeId);
      setUsSelected(u[0].name);
    }
  }, [usList, userData]);

  const onSubmit = formData => {
    if (loader) return;

    const month = formData.birthdate.getMonth() + 1;
    const day = formData.birthdate.getDate();
    const bDate = `${formData.birthdate.getFullYear()}-${month.toString().length > 1 ? month : '0'+month}-${day.toString().length > 1 ? day : '0'+day}`;
    const taxOfficeIdSelected = usList.filter(u => u.name === us);

    if (!us) {
      setUsError(true);
      return;
    }

    const dataObj = {
      id: userDataId,
      userId: data.user.id,
      isTaxResidence: radioVal === '1',
      taxNumber: formData.pesel,
      foreignTaxNumber: formData.taxpayerid,
      identityCard: formData.taxNrID,
      identityCardCountry: formData.taxNrIDCountry,
      dateOfBirth: bDate,
      province: formData.voivodeship,
      country: formData.country,
      county: formData.county,
      commune: formData.commune,
      city: formData.town,
      street: formData.street,
      houseNumber: formData.homenr,
      apartmentNumber: formData.localnr,
      postcode: formData.postcode,
      taxOfficeId: taxOfficeIdSelected[0].id,
      email: formData.email,
    };

    setLoader(true);

    JwtInterceptor
        .post(`${API_URL}Users/${data.user.id}/taxData`, dataObj)
        .then(() => {
          setPopUpText(t('saveSuccess'));
          setPopUpType('success');
          setIsEdit(false);
          setRefreshData(true);
        })
        .catch(function (error) {
          setPopUpText(t('errDefault'));
          setPopUpType('error');
        }).finally(() => {
          setLoader(false);
          setPopUp(true);
    });
  }

  const popUpCloseHandler = () => {
    setPopUp(false);
  }

  const handleSelect = (selectedItem) => {
    setUs(selectedItem);
    setUsError(false);
  };

  const radioHandler = (e) => {
    setRadioVal(e.currentTarget.value);
  }

  const editHandler = (e) => {
    e.preventDefault();
    setIsEdit(prevState => !prevState);
    reset();
    if (e.currentTarget.nodeName === 'svg') {
      setRadioVal(initRadioVal);
    }
  }

  return (
      <>
        <EditTitle editLinkText={t('edit')} text={t('taxFormTitle')} editFnc={(e) => editHandler(e)} isEdit={isEdit} isLoading={loader} sendFnc={handleSubmit(onSubmit)} />
        <form className="flex flex-col mt-6" onSubmit={handleSubmit(onSubmit)}>
          <label className="block mb-4 text-xs text-gray-400 text-formBorder">{t('taxFormTypeOfTaxLiability')}</label>
          <div className="flex text-base">
            <fieldset className="flex">
              <input id="resident" type="radio" name="switch" value="1" className="w-6 h-6 hover:cursor-pointer" checked={radioVal === '1'} onChange={(e) => radioHandler(e)} disabled={!isEdit} />
              <label htmlFor="resident" className="ml-1 hover:cursor-pointer">{t('taxFormResident')}</label>
            </fieldset>
          </div>
          <div className="flex text-base mt-4">
            <fieldset className="flex">
              <input id="noresident" type="radio" name="switch" value="2" className="w-6 h-6 hover:cursor-pointer" checked={radioVal === '2'} onChange={(e) => radioHandler(e)} disabled={!isEdit} />
              <label htmlFor="noresident" className="ml-1 hover:cursor-pointer">{t('taxFormNoResident')}</label>
            </fieldset>
          </div>
          {radioVal === '1' &&
            <div className="relative mt-10 float-label-input">
              <input type="text" id="pesel" defaultValue={userData.taxNumber} {...register("pesel", { required: true, disabled: !isEdit, maxLength: 11, pattern: /^[\d]{11}$/i})} maxLength="11" className={`block w-full lg:w-1/2 py-1.5 bg-white outline-none border-b border-solid border-grayBorder ${isEdit ? 'border-formBorder' : 'border-white'}`} />
              <label htmlFor="pesel" className="absolute top-1.5 left-0 text-gray-400 pointer-events-none transition duration-200 ease-in-outbg-white text-formBorder">{t('taxFormPesel')} *</label>
              {errors.pesel && errors.pesel.type === "required" && <span className="text-error">{t('errFieldRequired')}</span>}
              {errors.pesel && errors.pesel.type === "pattern" && <span className="text-error">{t('errFieldValue')}</span>}
            </div>
          }
          {radioVal === '2' &&
            <div className="relative mt-10 float-label-input">
              <input type="text" id="taxpayerid" defaultValue={userData.foreignTaxNumber} {...register("taxpayerid", { required: true, disabled: !isEdit, maxLength: 50, pattern: /^[^\s]+[^^<>&]{1,50}$/i})} maxLength="50" className={`block w-full lg:w-1/2 py-1.5 bg-white outline-none border-b border-solid border-grayBorder ${isEdit ? 'border-formBorder' : 'border-white'}`} />
              <label htmlFor="taxpayerid" className="absolute top-1.5 left-0 text-gray-400 pointer-events-none transition duration-200 ease-in-outbg-white text-formBorder">{t('taxTaxpayerID')} *</label>
              {errors.taxpayerid && errors.taxpayerid.type === "required" && <span className="text-error">{t('errFieldRequired')}</span>}
              {errors.taxpayerid && errors.taxpayerid.type === "pattern" && <span className="text-error">{t('errFieldValue')}</span>}
            </div>
          }
          <div className="lg:flex">
            <div className="relative mt-8 float-label-input lg:w-[24%] lg:mr-[2%]">
              <input type="text" id="taxNrID" defaultValue={userData && userData.identityCard ? userData.identityCard : ''} {...register("taxNrID", { required: true, disabled: !isEdit, maxLength: 50, pattern: /^[^\s]+[^^<>&]{1,50}$/i})} maxLength="50" className={`block w-full py-1.5 bg-white outline-none border-b border-solid border-grayBorder ${isEdit ? 'border-formBorder' : 'border-white'}`} />
              <label htmlFor="taxNrID" className="absolute top-1.5 left-0 text-gray-400 pointer-events-none transition duration-200 ease-in-outbg-white text-formBorder">{t('taxNrID')} *</label>
              {errors.taxNrID && errors.taxNrID.type === "required" && <span className="text-error">{t('errFieldRequired')}</span>}
              {errors.taxNrID && errors.taxNrID.type === "pattern" && <span className="text-error">{t('errFieldValue')}</span>}
            </div>
            <div className="relative mt-8 float-label-input lg:w-[24%]">
              <input type="text" id="taxNrIDCountry" defaultValue={userData && userData.identityCardCountry ? userData.identityCardCountry : ''} {...register("taxNrIDCountry", { required: true, disabled: !isEdit, minLength: 2, maxLength: 50, pattern: /^[^\s]+[^^<>&]{0,50}$/i})} maxLength="50" className={`block w-full py-1.5 bg-white outline-none border-b border-solid border-grayBorder ${isEdit ? 'border-formBorder' : 'border-white'}`} />
              <label htmlFor="taxNrIDCountry" className="absolute top-1.5 left-0 text-gray-400 pointer-events-none transition duration-200 ease-in-outbg-white text-formBorder">{t('taxNrIDCountry')} *</label>
              {errors.taxNrIDCountry && errors.taxNrIDCountry.type === "required" && <span className="text-error">{t('errFieldRequired')}</span>}
              {errors.taxNrIDCountry && errors.taxNrIDCountry.type === "pattern" && <span className="text-error">{t('errFieldValue')}</span>}
              {errors.taxNrIDCountry && errors.taxNrIDCountry.type === "minLength" && <span className="text-error">{t('errFieldLength')}</span>}
            </div>
          </div>
          <div className="lg:flex">
            <div className="relative mt-8 float-label-input lg:w-[24%] lg:mr-[2%]">
              <input type="text" id="lastName" defaultValue={data.user.lastName} {...register("lastName", { disabled: true, minLength: 2, maxLength: 50, pattern: /^[^\s]+[^^<>&]{1,50}$/i})} maxLength="50" className="block w-full py-1.5 bg-white outline-none border-b border-solid border-grayBorder border-white" />
              <label htmlFor="lastName" className="absolute top-1.5 left-0 text-gray-400 pointer-events-none transition duration-200 ease-in-outbg-white text-formBorder">{t('surname')} *</label>
              {errors.lastName && errors.lastName.type === "required" && <span className="text-error">{t('errFieldRequired')}</span>}
              {errors.lastName && errors.lastName.type === "pattern" && <span className="text-error">{t('errFieldValue')}</span>}
              {errors.lastName && errors.lastName.type === "minLength" && <span className="text-error">{t('errFieldLength')}</span>}
            </div>
            <div className="relative mt-8 float-label-input lg:w-[24%]">
              <input type="text" id="firstName" defaultValue={data.user.firstName} {...register("firstName", { disabled: true, minLength: 2, maxLength: 40, pattern: /^[^\s]+[^^<>&]{1,50}$/i})} maxLength="40" className="block w-full py-1.5 bg-white outline-none border-b border-solid border-grayBorder border-white" />
              <label htmlFor="firstName" className="absolute top-1.5 left-0 text-gray-400 pointer-events-none transition duration-200 ease-in-outbg-white text-formBorder">{t('nameContactForm')} *</label>
              {errors.firstName && errors.firstName.type === "required" && <span className="text-error">{t('errFieldRequired')}</span>}
              {errors.firstName && errors.firstName.type === "pattern" && <span className="text-error">{t('errFieldValue')}</span>}
              {errors.firstName && errors.firstName.type === "minLength" && <span className="text-error">{t('errFieldLength')}</span>}
            </div>
          </div>
          <div className="relative mt-8 float-label-input float-label-input--active w-full lg:w-1/2 datepicker-full">
            <Controller
                name="birthdate"
                control={control}
                defaultValue={birthdate}
                errors={errors}
                rules={{ required: true }}
                render={({ field: { onChange, value} }) => (
                    <DatePicker
                        type="text"
                        id="dateFrom"
                        placeholder=" "
                        dateFormat="dd-MM-yyyy"
                        className={`block w-full py-1.5 bg-white outline-none border-b border-solid border-grayBorder ${isEdit ? 'border-formBorder' : 'border-white'}`}
                        locale={dpickerLocal}
                        showMonthDropdown
                        showYearDropdown
                        dropdownMode="select"
                        selected={value}
                        onChange={
                          (date) => {
                            onChange(date);
                            setBirthdate(date);
                          }
                        }
                        disabled={!isEdit}
                    />
                )}
            />
            <label htmlFor="birthdate" className={`absolute top-1.5 left-0 text-gray-400 pointer-events-none transition duration-200 ease-in-outbg-white text-formBorder`}>{t('taxBirthDate')} *</label>
            {errors.birthdate && errors.birthdate.type === "required" && <span className="text-error">{t('errFieldRequired')}</span>}
            {errors.birthdate && errors.birthdate.type === "pattern" && <span className="text-error">{t('errFieldValue')}</span>}
          </div>
          <div className="lg:flex">
            <div className="relative mt-8 float-label-input lg:w-[24%] lg:mr-[2%]">
              <input type="text" id="country" defaultValue={userData && userData.country ? userData.country : ''} {...register("country", { required: true, disabled: !isEdit, minLength: 2, maxLength: 50, pattern: /^[^\s]+[^^<>&]{1,50}$/i})} maxLength="50" className={`block w-full py-1.5 bg-white outline-none border-b border-solid border-grayBorder ${isEdit ? 'border-formBorder' : 'border-white'}`} />
              <label htmlFor="country" className="absolute top-1.5 left-0 text-gray-400 pointer-events-none transition duration-200 ease-in-outbg-white text-formBorder">{t('taxCountry')} *</label>
              {errors.country && errors.country.type === "required" && <span className="text-error">{t('errFieldRequired')}</span>}
              {errors.country && errors.country.type === "pattern" && <span className="text-error">{t('errFieldValue')}</span>}
              {errors.country && errors.country.type === "minLength" && <span className="text-error">{t('errFieldLength')}</span>}
            </div>
            <div className="relative mt-8 float-label-input lg:w-[24%]">
              <input type="text" id="voivodeship" defaultValue={userData && userData.province ? userData.province : ''} {...register("voivodeship", { required: true, disabled: !isEdit, minLength: 2, maxLength: 50, pattern: /^[^\s]+[^^<>&]{1,50}$/i})} maxLength="50" className={`block w-full py-1.5 bg-white outline-none border-b border-solid border-grayBorder ${isEdit ? 'border-formBorder' : 'border-white'}`} />
              <label htmlFor="voivodeship" className="absolute top-1.5 left-0 text-gray-400 pointer-events-none transition duration-200 ease-in-outbg-white text-formBorder">{t('taxVoivodeship')} *</label>
              {errors.voivodeship && errors.voivodeship.type === "required" && <span className="text-error">{t('errFieldRequired')}</span>}
              {errors.voivodeship && errors.voivodeship.type === "pattern" && <span className="text-error">{t('errFieldValue')}</span>}
              {errors.voivodeship && errors.voivodeship.type === "minLength" && <span className="text-error">{t('errFieldLength')}</span>}
            </div>
          </div>
          <div className="lg:flex">
            <div className="relative mt-8 float-label-input lg:w-[24%] lg:mr-[2%]">
              <input type="text" id="county" defaultValue={userData && userData.county ? userData.county : ''} {...register("county", { required: true, disabled: !isEdit, minLength: 2, maxLength: 50, pattern: /^[^\s]+[^^<>&]{1,50}$/i})} maxLength="50" className={`block w-full py-1.5 bg-white outline-none border-b border-solid border-grayBorder ${isEdit ? 'border-formBorder' : 'border-white'}`} />
              <label htmlFor="county" className="absolute top-1.5 left-0 text-gray-400 pointer-events-none transition duration-200 ease-in-outbg-white text-formBorder">{t('taxCounty')} *</label>
              {errors.county && errors.county.type === "required" && <span className="text-error">{t('errFieldRequired')}</span>}
              {errors.county && errors.county.type === "pattern" && <span className="text-error">{t('errFieldValue')}</span>}
              {errors.county && errors.county.type === "minLength" && <span className="text-error">{t('errFieldLength')}</span>}
            </div>
            <div className="relative mt-8 float-label-input lg:w-[24%]">
              <input type="text" id="commune" defaultValue={userData && userData.commune ? userData.commune : ''} {...register("commune", { required: true, disabled: !isEdit, minLength: 2, maxLength: 50, pattern: /^[^\s]+[^^<>&]{1,50}$/i})} maxLength="50" className={`block w-full py-1.5 bg-white outline-none border-b border-solid border-grayBorder ${isEdit ? 'border-formBorder' : 'border-white'}`} />
              <label htmlFor="commune" className="absolute top-1.5 left-0 text-gray-400 pointer-events-none transition duration-200 ease-in-outbg-white text-formBorder">{t('taxCommune')} *</label>
              {errors.commune && errors.commune.type === "required" && <span className="text-error">{t('errFieldRequired')}</span>}
              {errors.commune && errors.commune.type === "pattern" && <span className="text-error">{t('errFieldValue')}</span>}
              {errors.commune && errors.commune.type === "minLength" && <span className="text-error">{t('errFieldLength')}</span>}
            </div>
          </div>
          <div className="lg:flex">
            <div className="relative mt-8 float-label-input lg:w-[24%] lg:mr-[2%]">
              <input type="text" id="street" defaultValue={userData && userData.street ? userData.street : ''} {...register("street", { required: true, disabled: !isEdit, minLength: 2, maxLength: 50, pattern: /^[^\s]+[^^<>&]{1,50}$/i})} maxLength="50" className={`block w-full py-1.5 bg-white outline-none border-b border-solid border-grayBorder ${isEdit ? 'border-formBorder' : 'border-white'}`} />
              <label htmlFor="street" className="absolute top-1.5 left-0 text-gray-400 pointer-events-none transition duration-200 ease-in-outbg-white text-formBorder">{t('taxStreet')} *</label>
              {errors.street && errors.street.type === "required" && <span className="text-error">{t('errFieldRequired')}</span>}
              {errors.street && errors.street.type === "pattern" && <span className="text-error">{t('errFieldValue')}</span>}
              {errors.street && errors.street.type === "minLength" && <span className="text-error">{t('errFieldLength')}</span>}
            </div>
            <div className="relative mt-8 float-label-input lg:w-[11%] lg:mr-[2%]">
              <input type="text" id="homenr" defaultValue={userData && userData.houseNumber ? userData.houseNumber : ''} {...register("homenr", { required: true, disabled: !isEdit, maxLength: 20, pattern: /^[^^<>&\s][a-zA-Z0-9 \/]{0,20}$/i})} maxLength="20" className={`block w-full py-1.5 bg-white outline-none border-b border-solid border-grayBorder ${isEdit ? 'border-formBorder' : 'border-white'}`} />
              <label htmlFor="homenr" className="absolute top-1.5 left-0 text-gray-400 pointer-events-none transition duration-200 ease-in-outbg-white text-formBorder">{t('taxHomeNr')} *</label>
              {errors.homenr && errors.homenr.type === "required" && <span className="text-error">{t('errFieldRequired')}</span>}
              {errors.homenr && errors.homenr.type === "pattern" && <span className="text-error">{t('errFieldValue')}</span>}
            </div>
            <div className="relative mt-8 float-label-input lg:w-[11%]">
              <input type="text" id="localnr" defaultValue={userData && userData.apartmentNumber ? userData.apartmentNumber : ''} {...register("localnr", { required: false, disabled: !isEdit, maxLength: 20, pattern: /^[^^<>&\s][a-zA-Z0-9 \/]{0,20}$/i})} maxLength="20" className={`block w-full py-1.5 bg-white outline-none border-b border-solid border-grayBorder ${isEdit ? 'border-formBorder' : 'border-white'}`} />
              <label htmlFor="localnr" className="absolute top-1.5 left-0 text-gray-400 pointer-events-none transition duration-200 ease-in-outbg-white text-formBorder">{t('taxLocalNr')}</label>
              {errors.localnr && errors.localnr.type === "pattern" && <span className="text-error">{t('errFieldValue')}</span>}
            </div>
          </div>
          <div className="lg:flex">
            <div className="relative mt-8 float-label-input lg:w-[14%] lg:mr-[2%]">
              <input type="text" id="postcode" defaultValue={userData && userData.postcode ? userData.postcode : ''} {...register("postcode", { required: true, disabled: !isEdit, minLength: 6, maxLength: 6, pattern: /^\d{2}-\d{3}$/i})} maxLength="6" className={`block w-full py-1.5 bg-white outline-none border-b border-solid border-grayBorder ${isEdit ? 'border-formBorder' : 'border-white'}`} />
              <label htmlFor="postcode" className="absolute top-1.5 left-0 text-gray-400 pointer-events-none transition duration-200 ease-in-outbg-white text-formBorder">{t('taxPostCode')} *</label>
              {errors.postcode && errors.postcode.type === "required" && <span className="text-error">{t('errFieldRequired')}</span>}
              {errors.postcode && errors.postcode.type === "pattern" && <span className="text-error">{t('errFieldValuePostCode')}</span>}
              {errors.postcode && errors.postcode.type === "minLength" && <span className="text-error">{t('errFieldLength6')}</span>}
            </div>
            <div className="relative mt-8 float-label-input lg:w-[34%]">
              <input type="text" id="town" defaultValue={userData && userData.city ? userData.city : ''} {...register("town", { required: true, disabled: !isEdit, minLength: 2, maxLength: 50, pattern: /^[^\s]+[^^<>&]{1,50}$/i})} maxLength="50" className={`block w-full py-1.5 bg-white outline-none border-b border-solid border-grayBorder ${isEdit ? 'border-formBorder' : 'border-white'}`} />
              <label htmlFor="town" className="absolute top-1.5 left-0 text-gray-400 pointer-events-none transition duration-200 ease-in-outbg-white text-formBorder">{t('taxTown')} *</label>
              {errors.town && errors.town.type === "required" && <span className="text-error">{t('errFieldRequired')}</span>}
              {errors.town && errors.town.type === "pattern" && <span className="text-error">{t('errFieldValue')}</span>}
              {errors.town && errors.town.type === "minLength" && <span className="text-error">{t('errFieldLength')}</span>}
            </div>
          </div>
          <div className="relative mt-8 float-label-input">
            <input type="text" id="email" defaultValue={userData && userData.email ? userData.email : ''} {...register("email", { required: true, disabled: !isEdit, maxLength: 80, pattern: /^[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z-]+)+$/i})} maxLength="80" className={`block w-full lg:w-1/2 py-1.5 bg-white outline-none border-b border-solid border-grayBorder ${isEdit ? 'border-formBorder' : 'border-white'}`} />
            <label htmlFor="email" className="absolute top-1.5 left-0 text-gray-400 pointer-events-none transition duration-200 ease-in-outbg-white text-formBorder">{t('emailAdressPrivate')} *</label>
            {errors.email && errors.email.type === "required" && <span className="text-error">{t('errFieldRequired')}</span>}
            {errors.email && errors.email.type === "pattern" && <span className="text-error">{t('errFieldEmail')}</span>}
          </div>
          <AutocompleteInput
              suggestions={suggestions}
              onSelect={handleSelect}
              label={t('taxOffice')}
              value={usSelected}
              reset={reset}
              isEdit={isEdit}
          />
          {usError && <span className="text-error">{t('errFieldRequired')}</span>}
          {isEdit &&
            <button type="submit" className={`relative w-fit mt-8 p-4 border font-nouvelrbold text-black text-basee transition ease-in-out duration-300 hover:cursor-pointer hover:bg-brand hover:border-brand ${loader ? 'pr-12' : ''}`}>
              {t('save')}
              {loader && <Loader />}
            </button>
          }
        </form>
        <PopUp open={popUp} text={popUpText} type={popUpType} hideFnc={popUpCloseHandler} outsideHide={true} />
      </>
  );
}

export default TaxFormForm;